import { createContext } from 'react';
import { Style } from '@/constants';

export const MAX_TEXT_COMPONENT = 60;

export type ScrollDirection = 'up' | 'down';

// Moved from src/constants.ts (improvements/11-article-renderer-library-
// extraction.md) so the renderer has no dependency outside this directory;
// constants.ts re-exports both for its own (non-renderer) callers.
export type NavbarVisibility = 'visible' | 'hidden';

// Primary language subtags written right to left (`iw`/`ji` are the legacy
// codes for Hebrew/Yiddish). Matched on the primary subtag, so regional tags
// like "ar-SA", "he_IL" or "fa-IR" count too.
const RTL_LANGUAGES = new Set([
	'ar',
	'he',
	'iw',
	'fa',
	'ur',
	'yi',
	'ji',
	'ps',
	'sd',
	'ug',
	'dv',
	'ckb',
]);

export function isRtlLanguage(language?: string): boolean {
	if (!language) return false;
	const primary = language.trim().toLowerCase().split(/[-_]/)[0];
	return RTL_LANGUAGES.has(primary);
}

/**
 * The `dir` attribute for an element holding content in `language`. Set on
 * that element, it overrides the document's direction (`<html dir>`) for
 * everything inside it -- an Arabic article reads right to left inside an
 * English app, and the reverse. `undefined` when the language is unknown, so
 * the element inherits the document's direction instead of being forced
 * left-to-right.
 */
export function articleDirection(language?: string): 'rtl' | 'ltr' | undefined {
	if (!language) return undefined;
	return isRtlLanguage(language) ? 'rtl' : 'ltr';
}

export const ArticleContext = createContext<ArticleContextProps>({
	width: undefined,
	height: undefined,
	styles: new Map(),
	style: null,
	lang: 'en',
	isPrinting: false,
	onLightboxTargetClick: () => {},
	onEventSend: () => {},
	onLightboxClick: () => {},
});

export interface ArticleContextProps {
	width: number | undefined;
	height: number | undefined;
	styles: Map<string, Style>;
	// A single `Style` looked up by id (Article.tsx's SingleArticle
	// resolves `article.style` through `styles.get(...)`), not
	// `Style.properties` -- some readers (e.g. Offer.tsx's
	// `style?.canvas?...`) access it as if it were already `.properties`,
	// a pre-existing mismatch not reconciled by this pass (same
	// CSS-generation-engine category as Style.properties itself).
	style: Style | null;
	isPrinting?: boolean;
	lang: string;
	onLightboxTargetClick?: (args: {
		id: string;
		resource: string;
		target: string;
	}) => void;
	onEventSend?: (args: {
		event: string;
		data: {
			url: string;
			componentId: string;
			componentType: string | undefined;
		};
	}) => void;
	onLightboxClick?: (args: { id: string }) => void;
}

export const CanvasContext = createContext<CanvasContextProps>({
	paddingLeft: null,
	paddingRight: null,
});

interface CanvasContextProps {
	paddingLeft?: string | number | null;
	paddingRight?: string | number | null;
}

export const ArticlesContext = createContext<ArticlesContext>({
	selectedIndex: null,
	lang: 'en',
	isTouchDevice: false,
	styles: new Map(),
});

interface ArticlesContext {
	selectedIndex: number | null;
	lang: string;
	setScrollDirection?: (direction: ScrollDirection) => void;
	isTouchDevice: boolean;
	styles: Map<string, Style>;
}
